/**
 * Campaign Gear Modal — DOM modal for purchasing team-wide gear upgrades.
 * Follows campaignStore.ts pattern: module state, show/hide/update, tab rendering.
 */
import type { CampaignState, WeaponKey, SharedGearKey, ItemGearKey } from '../types';
import { GEAR_UPGRADES, MAX_GEAR_TIER } from '../constants';
import { cur } from './flavor';
import { theme } from './theme';

// =============================================================================
// MODULE STATE
// =============================================================================

let modalEl: HTMLDivElement | null = null;
let contentEl: HTMLDivElement | null = null;
let headerMoneyEl: HTMLSpanElement | null = null;
let activeTab: 'launcher' | 'suit' = 'launcher';

let currentState: CampaignState | null = null;
let currentCallbacks: {
  onPurchaseGear: (gearKey: string) => void;
  onClose: () => void;
} | null = null;

// =============================================================================
// SHOW / HIDE / UPDATE
// =============================================================================

export function showCampaignGear(
  state: CampaignState,
  callbacks: {
    onPurchaseGear: (gearKey: string) => void;
    onClose: () => void;
  }
): void {
  currentState = state;
  currentCallbacks = callbacks;
  activeTab = 'launcher';

  if (!modalEl) {
    createModal();
  }

  modalEl!.style.display = 'flex';
  renderContent();
}

export function updateCampaignGear(state: CampaignState): void {
  currentState = state;
  if (!modalEl || modalEl.style.display === 'none') return;
  updateHeaderMoney();
  renderContent();
}

export function hideCampaignGear(): void {
  if (modalEl) {
    modalEl.style.display = 'none';
  }
}

// =============================================================================
// DOM CREATION
// =============================================================================

function createModal(): void {
  modalEl = document.createElement('div');
  modalEl.id = 'campaign-gear-modal';
  modalEl.style.cssText = `
    position:fixed; inset:0; z-index:106;
    display:flex; align-items:center; justify-content:center;
    background:${theme.surface.overlay};
    font-family:'Space Grotesk',monospace;
  `;
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) currentCallbacks?.onClose();
  });

  const panel = document.createElement('div');
  panel.style.cssText = `
    width:560px; max-height:80vh; background:${theme.surface.panelBg};
    border:1px solid ${theme.surface.border}; border-radius:10px;
    display:flex; flex-direction:column; overflow:hidden;
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    padding:14px 18px; display:flex; align-items:center; justify-content:space-between;
    border-bottom:1px solid ${theme.goldBorder20};
  `;
  header.innerHTML = `
    <div style="display:flex; align-items:center; gap:16px;">
      <span style="font-size:15px; color:${theme.accent}; font-weight:700; letter-spacing:2px;">GEAR</span>
      <span id="campaign-gear-money" style="font-size:12px; color:${theme.accent};"></span>
    </div>
    <button id="campaign-gear-close" style="
      background:none; border:none; color:${theme.text.secondary}; font-size:18px; cursor:pointer;
      padding:4px 8px; line-height:1;
    ">&times;</button>
  `;
  panel.appendChild(header);

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.id = 'campaign-gear-tabs';
  tabBar.style.cssText = `
    display:flex; border-bottom:1px solid ${theme.goldBorder15}; flex-shrink:0;
  `;
  const tabs: Array<{ key: typeof activeTab; label: string }> = [
    { key: 'launcher', label: 'LAUNCHER' },
    { key: 'suit', label: 'SUIT' },
  ];
  for (const tab of tabs) {
    const btn = document.createElement('button');
    btn.dataset.gearTab = tab.key;
    btn.textContent = tab.label;
    btn.style.cssText = `
      flex:1; padding:10px 0; background:none; border:none; border-bottom:2px solid transparent;
      color:${theme.text.secondary}; font-family:'Space Grotesk',monospace; font-size:11px;
      cursor:pointer; letter-spacing:1px; transition:color 0.15s, border-color 0.15s;
    `;
    btn.addEventListener('click', () => {
      activeTab = tab.key as typeof activeTab;
      renderContent();
    });
    tabBar.appendChild(btn);
  }
  panel.appendChild(tabBar);

  // Content
  contentEl = document.createElement('div');
  contentEl.style.cssText = `
    flex:1; overflow-y:auto; padding:14px 18px; min-height:200px;
  `;
  panel.appendChild(contentEl);

  modalEl.appendChild(panel);
  document.body.appendChild(modalEl);

  document.getElementById('campaign-gear-close')?.addEventListener('click', () => {
    currentCallbacks?.onClose();
  });

  headerMoneyEl = document.getElementById('campaign-gear-money') as HTMLSpanElement;
}

// =============================================================================
// RENDERING
// =============================================================================

function updateHeaderMoney(): void {
  if (headerMoneyEl && currentState) {
    headerMoneyEl.textContent = `${currentState.playerMoney} ${cur()}`;
  }
}

function renderContent(): void {
  if (!contentEl || !currentState) return;
  updateHeaderMoney();
  updateTabStyles();

  switch (activeTab) {
    case 'launcher': renderSlotTab('launcher'); break;
    case 'suit': renderSlotTab('suit'); break;
  }
}

function updateTabStyles(): void {
  const tabBar = document.getElementById('campaign-gear-tabs');
  if (!tabBar) return;
  for (const btn of tabBar.children) {
    const el = btn as HTMLButtonElement;
    const isActive = el.dataset.gearTab === activeTab;
    el.style.color = isActive ? theme.accent : theme.text.secondary;
    el.style.borderBottomColor = isActive ? theme.accent : 'transparent';
  }
}

/** Current tier of a gear entry, whichever storage bucket it lives in. */
function gearTierOf(gear: CampaignState['inventory']['gearUpgrades'], key: string): number {
  const def = GEAR_UPGRADES[key];
  if (!def) return 0;
  if (def.category === 'shared') return gear.sharedTiers[key as SharedGearKey] ?? 0;
  if (def.category === 'item') return gear.itemTiers[key as ItemGearKey] ?? 0;
  return gear.weaponTiers[key as WeaponKey] ?? 0;
}

/** One kit slot: every gear entry that feeds it (core/unitModifiers.ts LAUNCHER_GEAR / SUIT_GEAR). */
function renderSlotTab(slot: 'launcher' | 'suit'): void {
  if (!contentEl || !currentState) return;
  const gear = currentState.inventory.gearUpgrades;
  const keys = Object.keys(GEAR_UPGRADES).filter(k => GEAR_UPGRADES[k].slot === slot);
  const blurb = slot === 'launcher'
    ? 'The weapon mount. Range, yield, blast and probe reach apply to every weapon the team fires; the ballistic computer lengthens the aim preview.'
    : 'What the unit wears. Armor takes a flat amount off every hit; the medkit unlocks healthkits and makes them stronger.';
  let html = `<div style="font-size:10px; color:${theme.text.secondary}; margin-bottom:10px; line-height:1.5;">${blurb}</div>`;
  for (const key of keys) html += renderUpgradeRow(key, gearTierOf(gear, key));
  contentEl.innerHTML = html;
  wireButtons();
}

function renderUpgradeRow(gearKey: string, currentTier: number): string {
  const def = GEAR_UPGRADES[gearKey];
  const maxed = currentTier >= MAX_GEAR_TIER;
  const nextTierDef = maxed ? null : def.tiers[currentTier];
  const canAfford = nextTierDef ? (currentState?.playerMoney ?? 0) >= nextTierDef.cost : false;

  // Tier pips
  let pips = '';
  for (let i = 0; i < MAX_GEAR_TIER; i++) {
    const filled = i < currentTier;
    pips += `<span style="
      display:inline-block; width:10px; height:10px; border-radius:50%; margin-right:3px;
      background:${filled ? theme.accent : 'transparent'};
      border:1px solid ${filled ? theme.accent : theme.text.muted};
    "></span>`;
  }

  let actionHtml: string;
  if (maxed) {
    actionHtml = `<span style="font-size:10px; color:${theme.accent}; letter-spacing:1px;">MAXED</span>`;
  } else {
    actionHtml = `
      <span style="font-size:10px; color:${theme.text.secondary}; margin-right:8px;">
        ${nextTierDef!.label}
      </span>
      <button data-buy-gear="${gearKey}" style="
        padding:4px 12px; border:1px solid ${canAfford ? theme.accent : theme.text.disabled};
        background:${canAfford ? theme.goldBg12 : 'transparent'};
        color:${canAfford ? theme.accent : theme.text.disabled}; font-family:'Space Grotesk',monospace;
        font-size:11px; cursor:${canAfford ? 'pointer' : 'default'}; border-radius:3px;
        ${canAfford ? '' : 'opacity:0.5;'}
      ">${nextTierDef!.cost} ${cur()}</button>
    `;
  }

  return `<div style="
    display:flex; align-items:center; justify-content:space-between;
    padding:10px 12px; border-bottom:1px solid ${theme.surface.borderDim};
  ">
    <div style="display:flex; align-items:center; gap:10px; flex:1; min-width:0;">
      <span style="font-size:16px; width:24px; text-align:center;">${def.icon}</span>
      <div style="min-width:0;">
        <div style="font-size:12px; color:${theme.text.primary}; font-weight:600;">${def.name}</div>
        <div style="font-size:10px; color:${theme.text.secondary};">${def.description}</div>
      </div>
    </div>
    <div style="display:flex; align-items:center; gap:8px; flex-shrink:0;">
      <div style="display:flex; align-items:center; margin-right:8px;">${pips}</div>
      ${actionHtml}
    </div>
  </div>`;
}

function wireButtons(): void {
  if (!contentEl) return;
  contentEl.querySelectorAll('[data-buy-gear]').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = (btn as HTMLElement).dataset.buyGear!;
      currentCallbacks?.onPurchaseGear(key);
    });
  });
}
